// AI Fault Analytics frontend types (sub-plan 19). These mirror the camelCase wire shapes
// owned by sub-plan 16 / pinned by REF-08 §3.1. The backend contract wins: if 16 changes a
// field name, these types are reconciled, never the reverse. The page is a pure consumer.

// The 7 REF-07 s.3 root-cause categories. A JSON-`null` "unclassified" bucket can also appear
// in `byCategory` (running/failed/refused investigations leave root_cause_category null), so the
// category field is `string | null` on the wire and label helpers fall back to the raw value.
export type RootCauseCategory =
  | 'software_bug'
  | 'hardware_fault'
  | 'firmware_issue'
  | 'config'
  | 'user_error'
  | 'network'
  | 'unknown';

// The 4 REF-07 s.5 investigation statuses.
export type InvestigationStatus = 'running' | 'completed' | 'failed' | 'refused';

export type Granularity = 'day' | 'week';

export interface CategoryCount {
  category: RootCauseCategory | string | null;
  count: number;
}

export interface StatusCount {
  status: InvestigationStatus | string;
  count: number;
}

// A single UTC-bucketed point in the embedded time series. `bucket` is a server-formatted
// UTC calendar key (`YYYY-MM-DD` for day, the UTC-Monday `YYYY-MM-DD` for week) and is rendered
// verbatim — never passed through a timezone-converting parser (REF-08 §3.1).
export interface TimeSeriesPoint {
  bucket: string;
  count: number;
}

// GET /api/backend/ai-troubleshooting/analytics/summary response (REF-08 §3.1).
// Field is `total` (NOT `totalInvestigations`); `refused` is derived from byStatus (no top-level
// refusedCount); timeSeries is EMBEDDED here (no separate /timeseries endpoint).
export interface AnalyticsSummary {
  range: { from: string; to: string; granularity: Granularity };
  total: number;
  byCategory: CategoryCount[];
  byStatus: StatusCount[];
  timeSeries: TimeSeriesPoint[];
  handoffCount: number;
}

// A single flat recurring-fault row grouped jointly by (category, chargerModel, site).
// Wire field is `chargerModel` (NOT `model`); siteId/siteName/chargerModel may be null (REF-08 §3.1
// — a super-admin 'all'-scope row whose anchor is non-charger and has a null anchor_site_id).
export interface RecurringFaultRow {
  category: RootCauseCategory | string | null;
  chargerModel: string | null;
  siteId: string | null;
  siteName: string | null;
  count: number;
  lastSeenAt: string;
}

// GET /api/backend/ai-troubleshooting/analytics/recurring-faults response (REF-08 §3.1).
// The backend returns ONLY the flat `faults[]`; sub-plan 19 derives the per-category / per-model /
// per-site rollups client-side. `categories` / `totalGroups` are optional echo fields.
export interface RecurringFaults {
  range: { from: string; to: string };
  categories?: string[];
  faults: RecurringFaultRow[];
  totalGroups?: number;
}

// Combined payload the page stores after the two parallel fetches.
export interface AnalyticsData {
  summary: AnalyticsSummary;
  recurring: RecurringFaults;
}

// ---- Client-derived rollup row types (computed by RecurringFaultsSection from faults[]) ----

export interface CategoryRollup {
  category: RootCauseCategory | string | null;
  count: number;
  lastSeenAt: string;
}

export interface ModelRollup {
  chargerModel: string | null;
  count: number;
  lastSeenAt: string;
}

export interface SiteRollup {
  siteId: string | null;
  siteName: string | null;
  count: number;
  lastSeenAt: string;
}

// Polyglot raw-string fallback: a missing i18n key renders the wire value (no fabricated label).
type Translate = (key: string, options?: Record<string, unknown>) => string;

export const categoryLabel = (translate: Translate, c: string | null): string => {
  if (c === null || c === '') {
    return translate('aiTroubleshooting.analytics.category.unclassified', { _: 'Unclassified' });
  }
  return translate(`aiTroubleshooting.analytics.category.${c}`, { _: c });
};

export const statusLabel = (translate: Translate, s: string): string =>
  translate(`aiTroubleshooting.analytics.status.${s}`, { _: s });

// The "null model" fallback label for the per-model rollup (a charger model can be null).
export const modelLabel = (translate: Translate, m: string | null): string =>
  m === null || m === ''
    ? translate('aiTroubleshooting.analytics.unknownModel', { _: 'Unknown model' })
    : m;

// Stable color palette (reused reports idiom): 7 colors for the 7 categories; callers index modulo.
export const CATEGORY_COLORS = [
  '#009A75',
  '#2196F3',
  '#FFB800',
  '#FF6B6B',
  '#9C27B0',
  '#00BCD4',
  '#3F51B5',
];

// ---- Client-side rollup derivation (pure; unit-tested directly) ----

const maxIso = (a: string, b: string): string => (a > b ? a : b);

export function rollupByCategory(faults: RecurringFaultRow[]): CategoryRollup[] {
  const map = new Map<string, CategoryRollup>();
  for (const f of faults) {
    // Use a sentinel key for the null category so it groups into one "unclassified" bucket.
    const key = f.category === null ? ' null' : f.category;
    const existing = map.get(key);
    if (existing) {
      existing.count += f.count;
      existing.lastSeenAt = maxIso(existing.lastSeenAt, f.lastSeenAt);
    } else {
      map.set(key, { category: f.category, count: f.count, lastSeenAt: f.lastSeenAt });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

export function rollupByModel(faults: RecurringFaultRow[]): ModelRollup[] {
  const map = new Map<string, ModelRollup>();
  for (const f of faults) {
    const key = f.chargerModel === null ? ' null' : f.chargerModel;
    const existing = map.get(key);
    if (existing) {
      existing.count += f.count;
      existing.lastSeenAt = maxIso(existing.lastSeenAt, f.lastSeenAt);
    } else {
      map.set(key, { chargerModel: f.chargerModel, count: f.count, lastSeenAt: f.lastSeenAt });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

export function rollupBySite(faults: RecurringFaultRow[]): SiteRollup[] {
  const map = new Map<string, SiteRollup>();
  for (const f of faults) {
    // Sentinel key for a null siteId so null-site rows group into ONE bucket (mirrors
    // rollupByCategory/rollupByModel) instead of colliding on a `null` Map key.
    const key = f.siteId === null ? ' null' : f.siteId;
    const existing = map.get(key);
    if (existing) {
      existing.count += f.count;
      existing.lastSeenAt = maxIso(existing.lastSeenAt, f.lastSeenAt);
      // Carry the first non-null siteName seen for this siteId.
      if (existing.siteName === null && f.siteName !== null) {
        existing.siteName = f.siteName;
      }
    } else {
      map.set(key, {
        siteId: f.siteId,
        siteName: f.siteName,
        count: f.count,
        lastSeenAt: f.lastSeenAt,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}
